import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import PrescriptionInterface from '../../interfaces/prescription';
import { AppState } from '../../store';

import DispensationCancellation from '../../components/application/Dispensation/Cancellation';
import DispensationHistory from '../../components/application/Dispensation/History';
import Menu from '../../components/application/Menu';
import PrescriptionItemList from '../../components/application/Prescription/ItemList';

import Button from '../../components/ui/Button';
import DialogModal, {
  DialogActions,
  DialogSection,
  DialogTitle,
} from '../../components/ui/DialogModal';
import Icon from '../../components/ui/Icon';
import LabelValueList, {
  ItemInterface as HeaderItemInterface,
} from '../../components/ui/List/LabelValue';
import Page from '../../components/ui/Page';
import Spinner from '../../components/ui/Spinner';
import Subtitle from '../../components/ui/Subtitle';
import Text from '../../components/ui/Text';

import * as Styles from './PrescriptionStyles';
import { getTransformedPrescriptionItems } from '../../store/prescription/selectors';
import { ToastProvider } from '../../components/ui/Toast';
import { trackClickDispense } from '../../monitoring/interaction/prescription-detail';
import { useTrackingProperties } from '../../monitoring/utils';

const modalStyle = {
  width: '500px',
};

export interface Props {
  readonly error: boolean;
  readonly loading: boolean;
  readonly prescription: PrescriptionInterface;
  readonly headerItems: HeaderItemInterface[];
  readonly dispensePrescription: Function;
  readonly dispensation: {
    errors: string[];
    loading: boolean;
  };
}

const Prescription: React.FC<Props> = ({
  error,
  loading,
  prescription,
  headerItems,
  dispensation,
  dispensePrescription,
}: Props) => {
  const [showConfirmDispensationModal, setShowConfirmDispensationModal] =
    useState(false);
  const canDispenseWithoutSign = useSelector(({ permissions }: AppState) => {
    if (!permissions.current_drugstore) {
      return false;
    }

    return Boolean(
      permissions.current_drugstore.chain.can_dispense_without_signed,
    );
  });
  const confirmModalRef = useRef<HTMLDivElement>(null!);

  // caso o a farmácia tenha habilitado a opção de dispensar receitas não assinadas
  // precisamos verificar se existe itens onde a receita precisa ser retida para mostrarmos
  // a modal de alerta para avisar o usuário que a receita não foi assinada
  // essa regra se aplica para qualquer medicamento de receituário diferente de simples
  const requireRecipeRetentionItems = useMemo(() => {
    return prescription.items.filter(
      (item) => item.recipe.code !== 'SIMPLES' && item.recipe.code !== null,
    );
  }, [prescription.items]);

  // caso a prescrição não estiver assinada e possuir itens que precisam de retenção de receita
  const shouldWarnAboutNotSignedPrescription =
    !prescription.signed && requireRecipeRetentionItems.length > 0;

  const { drugstoreId, chainId, drugstoreName, chainName, drugstoreUF } = useTrackingProperties();

  function getPartiallyDispensedItens(): number {
    return checkedItems.filter((item) => {
      if (item.selectedQuantity === 'all') return false;
      return item.selectedQuantity && item.selectedQuantity < (item.prescribedQuantity ?? 0);
    }).length;
  }


  const handleDispenseClick = (): void => {
    trackClickDispense({
      prescriptionId: prescription.id,
      drugstoreId,
      chainId,
      partiallyDispensedItens: getPartiallyDispensedItens(),
      drugstoreName,
      chainName,
      drugstoreUF,
    });
    if (canDispenseWithoutSign && shouldWarnAboutNotSignedPrescription) {
      setShowConfirmDispensationModal(true);
      return;
    }

    dispensePrescription();
  };

  const handleCancel = (): void => {
    setShowConfirmDispensationModal(false);
  };

  const handleContinue = (): void => {
    setShowConfirmDispensationModal(false);
    dispensePrescription();
  };

  const transformedItems = useSelector((state: AppState) =>
    getTransformedPrescriptionItems(state),
  );

  const checkedItems = transformedItems.filter((item) => item.checked);

  const showDispenseButton = checkedItems.length > 0;

  return (
    <Styles.Container>
      {!loading && <Menu />}

      <div
        className='tracking-confirm-dispensation-without-signature-modal'
        ref={confirmModalRef}
      >
        <DialogModal
          show={showConfirmDispensationModal}
          modalStyle={modalStyle}
        >
          <DialogTitle>Atenção</DialogTitle>
          <DialogSection>
            <Text color='black' as='div'>
              Essa receita não foi assinada digitalmente e possui medicamentos
              que exigem retenção da receita física assinada.
              {requireRecipeRetentionItems.length > 0 && (
                <Styles.List>
                  {requireRecipeRetentionItems.map((item) => (
                    <li key={item.id}>{item.name}</li>
                  ))}
                </Styles.List>
              )}
              Deseja dispensá-la mesmo assim?
            </Text>
          </DialogSection>
          <DialogActions>
            <Button
              type='button'
              size='small'
              variant='flat'
              onClick={handleCancel}
              className='tracking-confirm-dispensation-without-signature-button-cancel'
            >
              Cancelar
            </Button>
            <Button
              type='button'
              color='primary'
              size='small'
              variant='flat'
              onClick={handleContinue}
              className='tracking-confirm-dispensation-without-signature-button-confirm'
            >
              Continuar
            </Button>
          </DialogActions>
        </DialogModal>
      </div>

      <Page error={error} loading={loading}>
        <>

          <Styles.Header>
            <Text color='black' scale='subtitle2'>
              Receita Digital
            </Text>
            <Styles.Title>{prescription.token}</Styles.Title>

            <Styles.Actions>
              <LabelValueList items={headerItems} />
            </Styles.Actions>
          </Styles.Header>

          {!canDispenseWithoutSign && shouldWarnAboutNotSignedPrescription ? (
            <Styles.Box>
              <p>
                Prescrição não assinada digitalmente <Icon name='alert' />
              </p>
              Por esse motivo, medicamentos com necessidade de duas vias ou de
              tipo não identificado não poderão ser dispensados.
            </Styles.Box>
          ) : null}

          <div>
            <Subtitle>Prescrição</Subtitle>
            <PrescriptionItemList />
          </div>

          {dispensation.errors.length > 0 && (
            <Styles.Error type='error'>
              Não foi possível fazer a dispensação dos itens selecionados.
              {dispensation.errors.map((err: string) => (
                <Text color='red' key={err}>
                  - {err}
                </Text>
              ))}
            </Styles.Error>
          )}

          {
            showDispenseButton ? <div style={{
              zIndex: 2,
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              padding: '4px 8px',
              boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
            }}>
              <div style={{
                maxWidth: '4xl',
                margin: '0 auto',
              }}>
                <Button
                  disabled={dispensation.loading}
                  style={{
                    width: '100%',
                    maxWidth: '800px',
                    margin: '0 auto',
                    background: '#160F3D',
                    color: 'white',
                    padding: '24px'
                  }}
                  onClick={handleDispenseClick}
                >
                  {dispensation.loading ? (
                    <Spinner size='14px' />
                  ) : (
                    <div>

                      <span style={{
                        fontSize: '14px',
                      }}>Dispensar Itens Selecionados</span>
                    </div>
                  )}
                </Button>
              </div>
            </div> : null
          }
          {/* <Styles.Actions>

					</Styles.Actions> */}

          <div>
            <ToastProvider>
              <DispensationCancellation />
            </ToastProvider>
          </div>
          <div>
            <DispensationHistory />
          </div>
        </>
      </Page>
    </Styles.Container>
  );
};

export default Prescription;
